import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { generateChangeId } from "./applyChanges"
import type {
  AnalysisResult,
  Change,
  ChangeType,
  OptimizationResult,
} from "./optimization"

/**
 * Parse a raw change object from the AI response into a proper Change type
 */
function parseRawChange(rawChange: unknown, index: number): Change | null {
  if (!rawChange || typeof rawChange !== "object") {
    return null
  }

  const validTypes: ChangeType[] = [
    "clarity",
    "edge_case",
    "structure",
    "example",
    "logic",
    "general",
  ]

  const type = validTypes.includes(
    (rawChange as Record<string, unknown>).type as ChangeType,
  )
    ? ((rawChange as Record<string, unknown>).type as ChangeType)
    : "general"

  return {
    id: generateChangeId(),
    type,
    location: stringifyUnknownValue(
      (rawChange as Record<string, unknown>).location || `Change ${index + 1}`,
    ),
    originalText: stringifyUnknownValue(
      (rawChange as Record<string, unknown>).originalText || "",
    ),
    newText: stringifyUnknownValue(
      (rawChange as Record<string, unknown>).newText || "",
    ),
    explanation: stringifyUnknownValue(
      (rawChange as Record<string, unknown>).explanation ||
        "No explanation provided",
    ),
    basedOnFeedback: Array.isArray(
      (rawChange as Record<string, unknown>).basedOnFeedback,
    )
      ? (
          (rawChange as Record<string, unknown>).basedOnFeedback as unknown[]
        ).map(String)
      : undefined,
    confidence:
      typeof (rawChange as Record<string, unknown>).confidence === "number"
        ? ((rawChange as Record<string, unknown>).confidence as number)
        : 0.8,
  }
}

/**
 * Convert legacy response (improvedMetric only) to granular format
 */
function convertLegacyToGranular(
  originalPrompt: string,
  improvedMetric: string,
  explanation: string,
): Change[] {
  // If the prompts are the same, no changes
  if (originalPrompt.trim() === improvedMetric.trim()) {
    return []
  }

  // Create a single "general" change representing the full improvement
  return [
    {
      id: generateChangeId(),
      type: "general",
      location: "Full prompt",
      originalText: originalPrompt,
      newText: improvedMetric,
      explanation: explanation || "General improvements applied",
      confidence: 0.8,
    },
  ]
}

export function parseOptimizationResponse(
  response: string,
  originalPrompt?: string,
): OptimizationResult {
  try {
    let cleanedResponse = response.trim()

    // Remove markdown code blocks if present
    if (cleanedResponse.includes("```json")) {
      const match = cleanedResponse.match(/```json\s*([\s\S]*?)\s*```/)
      if (match) {
        cleanedResponse = match[1].trim()
      }
    } else if (cleanedResponse.includes("```")) {
      const match = cleanedResponse.match(/```\s*([\s\S]*?)\s*```/)
      if (match) {
        cleanedResponse = match[1].trim()
      }
    }

    // Extract JSON object if response has extra text
    if (!cleanedResponse.startsWith("{")) {
      const jsonMatch = cleanedResponse.match(/{[\s\S]*}/)
      if (jsonMatch) {
        cleanedResponse = jsonMatch[0]
      }
    }

    // Try parsing the cleaned response
    const parsed = JSON.parse(cleanedResponse) as Record<string, unknown> | null

    // Validate the parsed response has required fields
    if (parsed && typeof parsed === "object") {
      const rawImprovedMetric =
        parsed.improvedMetric ||
        parsed.improved_metric ||
        parsed.metric ||
        parsed.prompt
      const improvedMetric =
        typeof rawImprovedMetric === "string" ? rawImprovedMetric : ""
      const rawExplanation =
        parsed.explanation ||
        parsed.reason ||
        parsed.reasoning ||
        parsed.improvements
      const explanation =
        typeof rawExplanation === "string" ? rawExplanation : ""

      if (improvedMetric) {
        // Check if this is the new granular format with changes array
        let changes: Change[] = []
        let analysis: AnalysisResult | undefined

        if (Array.isArray(parsed.changes) && parsed.changes.length > 0) {
          // New granular format
          changes = (parsed.changes as unknown[])
            .map((c: unknown, i: number) => parseRawChange(c, i))
            .filter((c: Change | null): c is Change => c !== null)
        } else if (originalPrompt) {
          // Legacy format - convert to single change
          changes = convertLegacyToGranular(
            originalPrompt,
            improvedMetric.trim(),
            (explanation || "").trim(),
          )
        }

        // Parse analysis if present
        const rawAnalysis = parsed.analysis as
          | Record<string, unknown>
          | undefined
        if (rawAnalysis && typeof rawAnalysis === "object") {
          analysis = {
            criteriaIssues: Array.isArray(rawAnalysis.criteriaIssues)
              ? rawAnalysis.criteriaIssues.map(String)
              : [],
            logicIssues: Array.isArray(rawAnalysis.logicIssues)
              ? rawAnalysis.logicIssues.map(String)
              : [],
            missingEdgeCases: Array.isArray(rawAnalysis.missingEdgeCases)
              ? rawAnalysis.missingEdgeCases.map(String)
              : [],
            exampleGaps: Array.isArray(rawAnalysis.exampleGaps)
              ? rawAnalysis.exampleGaps.map(String)
              : [],
          }
        }

        return {
          improvedMetric: improvedMetric.trim(),
          explanation: explanation
            ? explanation.trim()
            : "Optimization completed successfully.",
          changes,
          analysis,
        }
      }
    }
  } catch {
    // Try to extract content from the response even if it's not valid JSON
    const lines = response.split("\n")
    let improvedMetric = ""
    let explanation = ""
    let inImprovedSection = false
    let inExplanationSection = false

    for (const line of lines) {
      const trimmedLine = line.trim()
      if (
        trimmedLine.toLowerCase().includes("improved") &&
        trimmedLine.includes(":")
      ) {
        inImprovedSection = true
        inExplanationSection = false
        const content = trimmedLine.split(":")[1]?.trim()
        if (content) {
          improvedMetric = content
        }
        continue
      }
      if (
        trimmedLine.toLowerCase().includes("explanation") &&
        trimmedLine.includes(":")
      ) {
        inImprovedSection = false
        inExplanationSection = true
        const content = trimmedLine.split(":")[1]?.trim()
        if (content) {
          explanation = content
        }
        continue
      }
      if (inImprovedSection && trimmedLine) {
        improvedMetric += (improvedMetric ? "\n" : "") + trimmedLine
      }
      if (inExplanationSection && trimmedLine) {
        explanation += (explanation ? "\n" : "") + trimmedLine
      }
    }

    if (improvedMetric) {
      const changes = originalPrompt
        ? convertLegacyToGranular(
            originalPrompt,
            improvedMetric.trim(),
            explanation.trim(),
          )
        : []

      return {
        improvedMetric: improvedMetric.trim(),
        explanation:
          explanation.trim() ||
          "Unable to parse optimization explanation from AI response.",
        changes,
      }
    }
  }

  // Final fallback: return the original response
  const fallbackChanges = originalPrompt
    ? convertLegacyToGranular(originalPrompt, response.trim(), "")
    : []

  return {
    improvedMetric: response.trim(),
    explanation: "Unable to parse optimization explanation from AI response.",
    changes: fallbackChanges,
  }
}
